import {
  type DamageEntry,
  InjuredPersonTypeEnum,
  type Injury
} from '@lmc/validations'
import type { StepOption } from '../../stepOptions'
import { trimmed, withoutBlanks } from '../../stepValues'

/** An injury as the step holds it; the service user's carries their body maps. */
export type InjuryEntryValue = Partial<Omit<Injury, 'personType'>> & {
  bodyMaps?: Array<string>
  personType: InjuredPersonTypeEnum
}

export type DamageEntryValue = Partial<DamageEntry> & Pick<DamageEntry, 'type'>

/** The people an injury can be recorded against, by type. */
export type InjuredPeopleOptions = {
  otherParties: Array<string>
  otherServiceUsers: Array<StepOption>
  teamMembers: Array<StepOption>
}

/** Whether the injury names its person; the app records some without one. */
export const hasPerson = ({
  name,
  personType,
  residentId,
  userId
}: InjuryEntryValue) => {
  switch (personType) {
    case InjuredPersonTypeEnum.otherServiceUser:
      return Boolean(residentId)
    case InjuredPersonTypeEnum.teamMember:
      return Boolean(userId)
    case InjuredPersonTypeEnum.otherParty:
      return Boolean(trimmed(name))
    default:
      return true
  }
}

/**
 * An injury as it is saved: only the identifying field its person type needs,
 * the treatment fields only when treatment was given, blanks dropped, and the
 * body maps left for `residentInjuries`.
 */
export const toInjury = ({
  details,
  name,
  personType,
  residentId,
  treatment,
  treatmentGiven,
  treatmentGivenBy,
  userId
}: InjuryEntryValue): Injury => ({
  personType,
  ...withoutBlanks({
    details: trimmed(details),
    name:
      personType === InjuredPersonTypeEnum.otherParty
        ? trimmed(name)
        : undefined,
    residentId:
      personType === InjuredPersonTypeEnum.otherServiceUser
        ? residentId
        : undefined,
    treatment: treatmentGiven ? trimmed(treatment) : undefined,
    treatmentGiven,
    treatmentGivenBy: treatmentGiven ? treatmentGivenBy : undefined,
    userId: personType === InjuredPersonTypeEnum.teamMember ? userId : undefined
  })
})

export const toDamageEntry = ({
  details,
  type
}: DamageEntryValue): DamageEntry => ({
  type,
  ...withoutBlanks({ details: trimmed(details) })
})
